import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { isValidElement } from 'react'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Press-scale (active:scale-* / brightness) intentionally NOT in the base.
  // Per Emil Kowalski's design-eng framework, press feedback is reserved for
  // substantial actions (Send, Save, Submit, primary CTA) — not chrome that's
  // clicked 50-200x/session. Actions default to `feedback="press"`; use
  // `feedback="none"` for frequently used navigation and chrome.
  // design reference control contract: fixed heights on the 28/32/36 ladder (h-7/h-8/h-9)
  // with NO vertical padding — flex centering keeps every control on the rhythm.
  // Labels are 13px/500 (text-subtitle-sm) with −1% tracking; radius is the 8px
  // control step (rounded-md = calc(var(--radius) − 2px)).
  // `select-none`: control labels are not selectable (design reference → Interaction
  // Craft); adopted from upstream base-nova alongside the aria-expanded open-
  // state washes on outline/secondary/ghost (menu triggers read as "held open"
  // using our own hover recipe colors).
  // Disabled recipe (design reference → Buttons): label drops to ink-tertiary
  // everywhere, but only variants with a FILLED resting surface step to surface-2 (with
  // borders dropping to hairline-faint) — ghost/link rest transparent and must
  // not gain a filled box when disabled, so their per-variant overrides below
  // carry the surface step instead of the base.
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md text-subtitle-sm font-medium tracking-[-0.01em] whitespace-nowrap transition-[color,background-color,border-color,box-shadow,opacity] duration-[var(--duration-fast)] ease-[var(--ease-out)] select-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-tertiary [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-secondary',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:bg-secondary',
        outline:
          'border border-border bg-card hover:bg-secondary hover:text-accent-foreground disabled:border-hairline-faint disabled:bg-secondary aria-expanded:bg-secondary aria-expanded:text-accent-foreground',
        secondary:
          'border border-border bg-card text-secondary-foreground hover:bg-secondary disabled:border-hairline-faint disabled:bg-secondary aria-expanded:bg-secondary',
        ghost:
          'text-muted-foreground hover:bg-secondary hover:text-foreground aria-expanded:bg-secondary aria-expanded:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        brand:
          'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-brand-hover active:bg-brand-pressed disabled:bg-secondary',
        // THE brand CTA (Send, Activate, Start) — at most one per view.
        // Same teal tokens as `brand` (kept as an alias for existing callers).
        accent:
          'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] hover:bg-brand-hover active:bg-brand-pressed disabled:bg-secondary',
      },
      size: {
        default: 'h-8 px-3.5',
        sm: 'h-7 rounded-md px-3',
        lg: 'h-9 rounded-md px-4',
        icon: 'hit-extend h-8 w-8',
        'icon-sm': 'hit-extend h-7 w-7',
        'icon-lg': 'hit-extend h-9 w-9',
      },
      feedback: {
        // No press feedback. Use for nav, chrome, list rows, tabs — anything
        // clicked frequently enough that animation creates motion fatigue.
        none: '',
        // Tactile press for substantial actions. Adds a 0.97 scale + brightness
        // dip on :active. Includes transform/filter in the transition list so
        // the animation is GPU-accelerated.
        press:
          'transition-[transform,filter,color,background-color,border-color,box-shadow,opacity] active:scale-[0.97] active:brightness-[0.92]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      // Default = press. <Button> instances are overwhelmingly action surfaces
      // (Send, Save, Submit). Chrome uses raw <button> tags or opts out
      // explicitly via feedback="none". Inverting this default would silently
      // break ~100 existing action sites; the right fix is opt-out for chrome.
      feedback: 'press',
    },
  }
)

/**
 * The one pending affordance for controls. design reference reserves generic spinners
 * for exactly this — local control feedback — while page, list, table and panel
 * loading belongs to `Skeleton`. Exported so surfaces that can't render a
 * `<Button>` (AlertDialog's Close-based action, which is styled with
 * `buttonVariants` rather than composed from Button) show the identical mark
 * instead of hand-rolling a second one.
 *
 * Decorative: the pending state is announced by the control's own `aria-busy`
 * and its label, so the spinner stays `aria-hidden`.
 */
function ButtonSpinner({ className }: { className?: string }) {
  return <Spinner className={cn('text-current', className)} size="sm" />
}

export interface ButtonProps
  extends Omit<ButtonPrimitive.Props, 'className'>,
    VariantProps<typeof buttonVariants> {
  className?: string
  /**
   * Long-running action in flight. Prefixes the label with the shared pending
   * spinner, marks the control `aria-busy`, and disables it so the action can't
   * be fired twice while it runs. The label should still say what is happening
   * ("Marking…", not "Mark") — the spinner alone doesn't survive reduced motion
   * or a screen reader.
   */
  loading?: boolean
}

function Button({
  className,
  variant,
  size,
  feedback,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  // Surface the resolved variant on the DOM so tests/QA can verify which
  // visual variant a button rendered without snapshotting class lists.
  const resolvedVariant = variant ?? 'default'
  // The global press rule in globals.css is opt-in via [data-press]; emit
  // the attribute when this Button uses the `press` feedback variant so
  // consumers don't have to remember a second flag.
  const resolvedFeedback = feedback ?? 'press'
  // Navigation links styled as buttons keep link semantics. Base UI's useButton
  // stamps role="button" on non-native render elements in the production build,
  // so explicitly restore role="link" for anchor renders. Plain non-native
  // renders (spans, divs) still get role="button" for a11y.
  const isAnchorRender =
    isValidElement<{ href?: unknown }>(props.render) &&
    props.render.props.href !== null &&
    props.render.props.href !== undefined
  const roleOverride = isAnchorRender && props.role === undefined ? { role: 'link' } : undefined
  return (
    <ButtonPrimitive
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, feedback, className }))}
      data-loading={loading ? '' : undefined}
      data-press={resolvedFeedback === 'press' ? '' : undefined}
      data-slot="button"
      data-variant={resolvedVariant}
      disabled={disabled || loading}
      {...props}
      {...roleOverride}
    >
      {/* Children are only re-wrapped while pending, so idle buttons render
          exactly the tree they did before `loading` existed. */}
      {loading ? (
        <>
          <ButtonSpinner />
          {children}
        </>
      ) : (
        children
      )}
    </ButtonPrimitive>
  )
}

export { Button, ButtonSpinner, buttonVariants }
